import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './addLaundry.css';
import LoadingPopup from '../../SharedView/LoadingPopup';

function AddLaundry() {
    const navigate = useNavigate();
    const [todayBookings, setTodayBookings] = useState([]);
    const [selectedBooking, setSelectedBooking] = useState('');
    const [dropOffItems, setDropOffItems] = useState([
        { name: 'Bedsheet', quantity: '' },
        { name: 'Duvet Cover', quantity: '' },
        { name: 'Pillowcase', quantity: '' },
        { name: 'Bath Towel', quantity: '' },
        { name: 'Hand Towel', quantity: '' },
        { name: 'Face Towel', quantity: '' },
        { name: 'Bathmat', quantity: '' }
    ]);
    const [pickupItems, setPickupItems] = useState([
        { name: 'Bedsheet', quantity: '' },
        { name: 'Duvet Cover', quantity: '' },
        { name: 'Pillowcase', quantity: '' },
        { name: 'Bath Towel', quantity: '' },
        { name: 'Hand Towel', quantity: '' },
        { name: 'Face Towel', quantity: '' },
        { name: 'Bathmat', quantity: '' }
    ]);

    /* A Super King unit counts its own linen (owner, 2026-09-03): selecting a
       booking whose property has SK beds adds the two SK rows to BOTH lists
       automatically — nobody should have to remember them as extras. Switching
       to a non-SK booking removes them again, unless a count was already typed. */
    const SK_ROWS = ['Super King Bedsheet', 'Super King Duvet Cover'];
    const [skAuto, setSkAuto] = useState(false);
    const syncSuperKingRows = (hasSk) => {
        setSkAuto(hasSk);
        const sync = (items) => {
            if (hasSk) {
                if (items.some((i) => SK_ROWS.includes(i.name))) return items;
                const at = items.findIndex((i) => i.name === 'Duvet Cover');
                const inserted = [...items];
                inserted.splice(at >= 0 ? at + 1 : items.length, 0,
                    ...SK_ROWS.map((name) => ({ name, quantity: '' })));
                return inserted;
            }
            return items.filter((i) => !SK_ROWS.includes(i.name) || String(i.quantity).trim() !== '');
        };
        setDropOffItems(sync);
        setPickupItems(sync);
    };

    // Extra items for Drop Off and Pickup
    const [dropOffExtraItems, setDropOffExtraItems] = useState([]);
    const [pickupExtraItems, setPickupExtraItems] = useState([]);
    const [selectedExtraDropOffItem, setSelectedExtraDropOffItem] = useState('');
    const [selectedExtraPickupItem, setSelectedExtraPickupItem] = useState('');
    const [comment, setComment] = useState('');
    const [extraItemOptions, setExtraItemOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [feedbackMessage, setFeedbackMessage] = useState('');
    const [isLinenHire, setIsLinenHire] = useState(false);



    useEffect(() => {
        const fetchTodayBookings = async () => {
            try {
                const response = await axios.get('/api/bookings/today');
                setTodayBookings(response.data);
            } catch (error) {
                console.error("Error fetching today's bookings:", error);
            }
        };

        const fetchLaundryItems = async () => {
            try {
                const response = await axios.get('/api/laundry/laundry-items');
                const filteredItems = response.data.filter(item => !["Bedsheet", "Duvet Cover", "Pillowcase", "Bath Towel", "Hand Towel", "Face Towel", "Bathmat"].includes(item.name));
                setExtraItemOptions(filteredItems.map(item => ({ value: item.name, label: item.name })));
            } catch (error) {
                console.error("Error fetching laundry items:", error);
            }
        };

        fetchTodayBookings();
        fetchLaundryItems();
    }, []);

    const handleQuantityChange = (e, index, list, setList) => {
        const updatedItems = [...list];
        updatedItems[index].quantity = e.target.value ? parseInt(e.target.value, 10) : '';
        setList(updatedItems);
    };

    const handleAddExtraItem = (selectedItem, setItems, items) => {
        if (selectedItem && !items.some(item => item.name === selectedItem.value)) {
            setItems([{ name: selectedItem.value, quantity: '' }, ...items]);
        }
    };

    const handleRemoveExtraItem = (index, setItems, items) => {
        const updatedItems = [...items];
        updatedItems.splice(index, 1);
        setItems(updatedItems);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const combinedItems = [
            ...dropOffItems,
            ...dropOffExtraItems,
            ...pickupItems,
            ...pickupExtraItems
        ];

        const hasAnyQuantity = combinedItems.some(item => parseInt(item.quantity) > 0);

        if (!hasAnyQuantity) {
            alert('You must enter at least one quantity before submitting.');
            return;
        }

        setIsLoading(true); // Start loading

        const filteredDropOffExtraItems = dropOffExtraItems.filter(item => item.quantity > 0);
        const filteredPickupExtraItems = pickupExtraItems.filter(item => item.quantity > 0);

        const laundryCountData = {
            booking_id: selectedBooking,
            is_laundry_count_lh: isLinenHire,
            drop_off_items: dropOffItems.concat(filteredDropOffExtraItems).map(item => ({
                name: item.name,
                quantity: item.quantity !== '' ? item.quantity : 0
            })),
            pickup_items: pickupItems.concat(filteredPickupExtraItems).map(item => ({
                name: item.name,
                quantity: item.quantity !== '' ? item.quantity : 0
            })),
            comment: comment
        };

        try {
            const response = await axios.post('/api/laundry', laundryCountData);
            if (response.status === 201) {
                setFeedbackMessage('Laundry count submitted successfully');
            }
        } catch (error) {
            if (
                error.response &&
                error.response.status === 400 &&
                error.response.data.error === 'Laundry count already exists for this booking.'
            ) {
                setFeedbackMessage('There is already a laundry count for this booking');
            } else {
                console.error('Error submitting laundry count:', error);
                setFeedbackMessage('Failed to submit laundry count');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handlePopupClose = () => {
        setFeedbackMessage('');
        navigate(`/laundry/${selectedBooking}`);
    };

    return (
        <div className="add-laundry-container">
            <h2>Add Laundry Count</h2>
            <form onSubmit={handleSubmit}>
                {/* Booking Selection */}
                <div className="form-group">
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                        {/* Left column: Select Booking */}
                        <div style={{ flex: 1 }}>
                            <label style={{ marginBottom: 8, display: 'block' }}>Select Booking (Today)</label>
                            <Select
                                options={todayBookings.map(booking => ({
                                    value: booking.booking_id,
                                    label: `#${String(booking.booking_id).padStart(4, '0')} - ${booking.client_name} - Unit ${booking.unit_number} - ${booking.building_name} (${booking.bedrooms} - ${booking.bathrooms}) - ${booking.booking_type}`,
                                    is_linen_hire: !!booking.is_linen_hire, // ensure API provides this
                                    super_king_beds: Number(booking.super_king_beds) || 0,
                                }))}
                                onChange={(selectedOption) => {
                                    setSelectedBooking(selectedOption.value);
                                    setIsLinenHire(!!selectedOption.is_linen_hire); // auto-default based on property
                                    syncSuperKingRows(selectedOption.super_king_beds > 0);
                                }}
                                placeholder="Select a Booking"
                                isSearchable
                                required
                                className="select-booking"
                            />
                        </div>

                        {/* Right column: Linen Hire (label + switch) */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                            <label style={{ marginBottom: 8, display: 'block' }}>Linen Hire</label>
                            <label className="switch" title={!selectedBooking ? 'Select a booking first' : 'Linen Hire'}>
                                <input
                                    type="checkbox"
                                    checked={isLinenHire}
                                    onChange={(e) => setIsLinenHire(e.target.checked)}
                                    disabled={!selectedBooking} // enable after selecting a booking
                                />
                                <span className="slider round"></span>
                            </label>
                        </div>
                    </div>
                </div>

                {/* Drop-off and Pickup Lists */}
                <div className="laundry-lists-container">
                    <div className="list-section">
                        <h3>Drop Off Items</h3>
                        {dropOffItems.map((item, index) => (
                            <div key={index} className="item-row">
                                <span className="item-name">{item.name}</span>
                                <input
                                    type="number"
                                    value={item.quantity}
                                    onChange={(e) => handleQuantityChange(e, index, dropOffItems, setDropOffItems)}
                                    placeholder="Quantity"
                                    className="quantity-input"
                                    onWheel={(e) => e.target.blur()} // Prevent changing value on scroll
                                    onKeyDown={(e) => {
                                        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                            e.preventDefault(); // Prevent changing value with arrow keys
                                        }
                                    }}
                                />
                            </div>
                        ))}

                        <h4>Add Extra Drop Off Items</h4>
                        {dropOffExtraItems.length > 0 && (
                            <div className="extra-items-list">
                                {dropOffExtraItems.map((item, index) => (
                                    <div key={index} className="item-row">
                                        <span className="item-name">{item.name}</span>
                                        <input
                                            type="number"
                                            value={item.quantity}
                                            onChange={(e) => handleQuantityChange(e, index, dropOffExtraItems, setDropOffExtraItems)}
                                            placeholder="Quantity"
                                            className="quantity-input"
                                            onWheel={(e) => e.target.blur()} // Prevent changing value on scroll
                                            onKeyDown={(e) => {
                                                if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                                    e.preventDefault(); // Prevent changing value with arrow keys
                                                }
                                            }}
                                        />
                                        <span
                                            className="remove-extra-item-button remove-icon"
                                            onClick={() => handleRemoveExtraItem(index, setDropOffExtraItems, dropOffExtraItems)}
                                            style={{ cursor: 'pointer', color: 'grey', fontSize: '1.5rem' }}
                                        >
                                            &times;
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className="extra-item-selection">
                            <Select
                                options={extraItemOptions.filter((o) => !(skAuto && SK_ROWS.includes(o.value)))}
                                onChange={(selectedOption) => setSelectedExtraDropOffItem(selectedOption)}
                                placeholder="Select Extra Item"
                                className="select-extra-item"
                            />
                            <button
                                type="button"
                                className="add-extra-item-button"
                                onClick={() => handleAddExtraItem(selectedExtraDropOffItem, setDropOffExtraItems, dropOffExtraItems)}
                            >
                                + Extra Item
                            </button>
                        </div>
                    </div>

                    <div className="list-section">
                        <h3>Pickup Items</h3>
                        {pickupItems.map((item, index) => (
                            <div key={index} className="item-row">
                                <span className="item-name">{item.name}</span>
                                <input
                                    type="number"
                                    value={item.quantity}
                                    onChange={(e) => handleQuantityChange(e, index, pickupItems, setPickupItems)}
                                    placeholder="Quantity"
                                    className="quantity-input"
                                    onWheel={(e) => e.target.blur()} // Prevent changing value on scroll
                                    onKeyDown={(e) => {
                                        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                            e.preventDefault(); // Prevent changing value with arrow keys
                                        }
                                    }}
                                />
                            </div>
                        ))}

                        <h4>Add Extra Pickup Items</h4>
                        {pickupExtraItems.length > 0 && (
                            <div className="extra-items-list">
                                {pickupExtraItems.map((item, index) => (
                                    <div key={index} className="item-row">
                                        <span className="item-name">{item.name}</span>
                                        <input
                                            type="number"
                                            value={item.quantity}
                                            onChange={(e) => handleQuantityChange(e, index, pickupExtraItems, setPickupExtraItems)}
                                            placeholder="Quantity"
                                            className="quantity-input"
                                            onWheel={(e) => e.target.blur()} // Prevent changing value on scroll
                                            onKeyDown={(e) => {
                                                if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                                    e.preventDefault(); // Prevent changing value with arrow keys
                                                }
                                            }}
                                        />
                                        <span
                                            className="remove-extra-item-button remove-icon"
                                            onClick={() => handleRemoveExtraItem(index, setPickupExtraItems, pickupExtraItems)}
                                            style={{ cursor: 'pointer', color: 'grey', fontSize: '1.5rem' }}
                                        >
                                            &times;
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className="extra-item-selection">
                            <Select
                                options={extraItemOptions.filter((o) => !(skAuto && SK_ROWS.includes(o.value)))}
                                onChange={(selectedOption) => setSelectedExtraPickupItem(selectedOption)}
                                placeholder="Select Extra Item"
                                className="select-extra-item"
                            />
                            <button
                                type="button"
                                className="add-extra-item-button"
                                onClick={() => handleAddExtraItem(selectedExtraPickupItem, setPickupExtraItems, pickupExtraItems)}
                            >
                                + Extra Item
                            </button>
                        </div>
                    </div>
                </div>

                {/* Comment Section */}
                <div className="form-group">
                    <label>Comment</label>
                    <textarea
                        rows="3"
                        placeholder="Add any comments related to this laundry count"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        className="comment-field"
                    />
                </div>

                {/* Submit Button */}
                <button type="submit" className="submit-button">Submit Laundry Count</button>
            </form>
            <LoadingPopup
                isLoading={isLoading}
                feedbackMessage={feedbackMessage}
                onClose={handlePopupClose}
            />
        </div>
    );
}

export default AddLaundry;
